import { getDashboardContacts } from "../../api/axios";
import { useState, useEffect, useMemo } from "react";
import { nf } from "../../utilities/Utils";
import useAuth from "../../hooks/useAuth";
import LoadingContainer from "../../components/LoadingContainer";
import {
  DashboardBars,
  DashboardMetric,
  DashboardPanel,
  getBrandLabel,
  getCountValue,
  getSourceLabel,
  sortByValue,
} from "./DashboardVisuals";

const DashboardContactsTable = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState(0);
  const [contactsByType, setContactsByType] = useState([]);
  const [contactsByBrand, setContactsByBrand] = useState([]);
  const [contactsBySource, setContactsBySource] = useState([]);
  const [contactsByActivations, setContactsByActivations] = useState([]);
  const [contactsByEvents, setContactsByEvents] = useState([]);
  const { auth } = useAuth();

  const paramsKey = useMemo(() => JSON.stringify(props?.params ?? {}), [props?.params]);
  const dashboardParams = useMemo(() => JSON.parse(paramsKey), [paramsKey]);
  const ownerCount = getCountValue(
    contactsByType.find((entry) => entry.segment_key === "owner_with_pet" || entry.contact_type_name === "Dueño de mascota")
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        const [
          totalContactsRes,
          contactsByTypeRes,
          contactsByBrandRes,
          contactsBySourceRes,
          contactsByActivationsRes,
          contactsByEventsRes,
        ] = await Promise.all([
          getDashboardContacts({ auth, params: { ...dashboardParams } }),
          getDashboardContacts({ auth, params: { ...dashboardParams, byContactType: true } }),
          getDashboardContacts({ auth, params: { ...dashboardParams, byBrand: true } }),
          getDashboardContacts({ auth, params: { ...dashboardParams, idParentSource: 0, bySource: true } }),
          getDashboardContacts({ auth, params: { ...dashboardParams, idParentSource: 11, bySource: true } }),
          getDashboardContacts({ auth, params: { ...dashboardParams, idParentSource: 6, bySource: true } }),
        ]);

        setContacts(totalContactsRes?.count ?? 0);
        setContactsByType(sortByValue(Array.isArray(contactsByTypeRes?.results) ? contactsByTypeRes.results : []));
        setContactsByBrand(sortByValue(Array.isArray(contactsByBrandRes?.results) ? contactsByBrandRes.results : []));
        setContactsBySource(sortByValue(Array.isArray(contactsBySourceRes?.results) ? contactsBySourceRes.results : []));
        setContactsByActivations(sortByValue(Array.isArray(contactsByActivationsRes?.results) ? contactsByActivationsRes.results : []));
        setContactsByEvents(sortByValue(Array.isArray(contactsByEventsRes?.results) ? contactsByEventsRes.results : []));
        setError(null);
      } catch (error) {
        setError(error);
      }

      setIsLoading(false);
    };

    fetchData();
  }, [auth, dashboardParams]);

  if (error) {
    return (
      <div className="dashboard-overview dashboard-detail">
        <div className="dashboard-empty">No se pudieron cargar los indicadores de contactos.</div>
      </div>
    );
  }

  return (
    <LoadingContainer isLoading={isLoading}>
      <div className="dashboard-overview dashboard-detail">
        <section className="dashboard-hero">
          <div className="dashboard-hero__intro">
            <span className="dashboard-eyebrow">Contactos</span>
            <h2 className="dashboard-hero__title">Audiencia del CRM</h2>
            <p className="dashboard-hero__copy">
              Lectura rápida de volumen, composición, marcas y origen de los registros.
            </p>
          </div>
          <div className="dashboard-hero__metrics">
            <DashboardMetric label="Total contactos" value={contacts} detail="base activa" accent="red" />
            <DashboardMetric label="Dueños con mascota" value={ownerCount} detail="segmento principal" accent="gold" />
            <DashboardMetric label="Marcas" value={contactsByBrand.length} detail="con contactos" accent="ink" />
            <DashboardMetric label="Fuentes" value={contactsBySource.length} detail="origen detectado" accent="sand" />
          </div>
        </section>

        <section className="dashboard-grid dashboard-detail-grid">
          <DashboardPanel kicker="Audiencia" title="Tipos de contacto" className="dashboard-panel--feature">
            <DashboardBars
              items={contactsByType}
              getLabel={(entry) => entry.contact_type_name ?? entry.label ?? "Sin tipo"}
              getValue={getCountValue}
            />
          </DashboardPanel>

          <DashboardPanel kicker="Marca" title="Contactos por marca">
            <DashboardBars
              items={contactsByBrand}
              getLabel={getBrandLabel}
              getValue={getCountValue}
              tone="gold"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Origen" title="Fuentes principales">
            <DashboardBars
              items={contactsBySource}
              getLabel={getSourceLabel}
              getValue={getCountValue}
              limit={10}
              tone="ink"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Origen" title="Activaciones">
            <DashboardBars
              items={contactsByActivations}
              getLabel={getSourceLabel}
              getValue={getCountValue}
              limit={10}
              tone="sand"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Origen" title="Eventos">
            <DashboardBars
              items={contactsByEvents}
              getLabel={getSourceLabel}
              getValue={getCountValue}
              limit={10}
            />
          </DashboardPanel>

          <DashboardPanel kicker="Síntesis" title="Lectura operativa">
            <div className="dashboard-scoreboard">
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Contactos</span>
                <strong className="dashboard-scoreboard__value">{nf(contacts)}</strong>
              </div>
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Tipos</span>
                <strong className="dashboard-scoreboard__value">{contactsByType.length}</strong>
              </div>
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Activaciones</span>
                <strong className="dashboard-scoreboard__value">{contactsByActivations.length}</strong>
              </div>
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Eventos</span>
                <strong className="dashboard-scoreboard__value">{contactsByEvents.length}</strong>
              </div>
            </div>
          </DashboardPanel>
        </section>
      </div>
    </LoadingContainer>
  );
};

export default DashboardContactsTable;
